/**
 * SCORM 1.2 (window.API) and SCORM 2004 (window.API_1484_11) runtime API.
 *
 * The content (SCO) runs in an iframe on the same origin and finds this
 * object in the parent window. Data model values are kept in memory and sent
 * to the server on Commit/Finish: the server decides when the lesson is
 * completed.
 */
import type { ScormVersion } from '@/api/lms'

export type CommitHandler = (values: Record<string, string>, finish: boolean) => void

const ERRORS_12: Record<string, string> = {
  '0': 'No error',
  '101': 'General exception',
  '201': 'Invalid argument error',
  '202': 'Element cannot have children',
  '203': 'Element not an array. Cannot have count',
  '301': 'Not initialized',
  '401': 'Not implemented error',
  '402': 'Invalid set value, element is a keyword',
  '403': 'Element is read only',
  '404': 'Element is write only',
  '405': 'Incorrect Data Type',
}

const ERRORS_2004: Record<string, string> = {
  '0': 'No Error',
  '101': 'General Exception',
  '103': 'Already Initialized',
  '104': 'Content Instance Terminated',
  '112': 'Termination Before Initialization',
  '113': 'Termination After Termination',
  '122': 'Retrieve Data Before Initialization',
  '123': 'Retrieve Data After Termination',
  '132': 'Store Data Before Initialization',
  '133': 'Store Data After Termination',
  '142': 'Commit Before Initialization',
  '143': 'Commit After Termination',
  '201': 'General Argument Error',
  '301': 'General Get Failure',
  '351': 'General Set Failure',
  '401': 'Undefined Data Model Element',
  '403': 'Data Model Element Value Not Initialized',
  '404': 'Data Model Element Is Read Only',
  '405': 'Data Model Element Is Write Only',
  '406': 'Data Model Element Type Mismatch',
  '407': 'Data Model Element Value Out Of Range',
}

const CHILDREN_12: Record<string, string> = {
  'cmi.core._children':
    'student_id,student_name,lesson_location,credit,lesson_status,entry,score,total_time,lesson_mode,exit,session_time',
  'cmi.core.score._children': 'raw,min,max',
  'cmi.objectives._children': 'id,score,status',
  'cmi.student_data._children': 'mastery_score,max_time_allowed,time_limit_action',
  'cmi.student_preference._children': 'audio,language,speed,text',
  'cmi.interactions._children':
    'id,objectives,time,type,correct_responses,weighting,student_response,result,latency',
}

const CHILDREN_2004: Record<string, string> = {
  'cmi.score._children': 'scaled,raw,min,max',
  'cmi.objectives._children': 'id,score,success_status,completion_status,progress_measure,description',
  'cmi.interactions._children':
    'id,type,objectives,timestamp,correct_responses,weighting,learner_response,result,latency,description',
  'cmi.learner_preference._children': 'audio_level,language,delivery_speed,audio_captioning',
  'cmi.comments_from_learner._children': 'comment,location,timestamp',
  'cmi.comments_from_lms._children': 'comment,location,timestamp',
}

const READ_ONLY_12 = new Set([
  'cmi._version', 'cmi.core.student_id', 'cmi.core.student_name', 'cmi.core.credit', 'cmi.core.entry',
  'cmi.core.total_time', 'cmi.core.lesson_mode', 'cmi.launch_data', 'cmi.comments_from_lms',
  'cmi.student_data.mastery_score', 'cmi.student_data.max_time_allowed', 'cmi.student_data.time_limit_action',
])

const READ_ONLY_2004 = new Set([
  'cmi._version', 'cmi.completion_threshold', 'cmi.credit', 'cmi.entry', 'cmi.launch_data',
  'cmi.learner_id', 'cmi.learner_name', 'cmi.max_time_allowed', 'cmi.mode', 'cmi.scaled_passing_score',
  'cmi.time_limit_action', 'cmi.total_time',
])

const WRITE_ONLY_12 = new Set(['cmi.core.exit', 'cmi.core.session_time'])
const WRITE_ONLY_2004 = new Set(['cmi.exit', 'cmi.session_time'])

const VOCABULARY: Record<string, string[]> = {
  'cmi.core.lesson_status': ['passed', 'completed', 'failed', 'incomplete', 'browsed'],
  'cmi.core.exit': ['time-out', 'suspend', 'logout', ''],
  'cmi.completion_status': ['completed', 'incomplete', 'not attempted', 'unknown'],
  'cmi.success_status': ['passed', 'failed', 'unknown'],
  'cmi.exit': ['time-out', 'suspend', 'logout', 'normal', ''],
}

const NUMERIC = /^-?\d+(\.\d+)?$/

export class ScormRuntime {
  readonly version: ScormVersion
  private values: Record<string, string>
  private readonly onCommit: CommitHandler
  private initialized = false
  private terminated = false
  private lastError = '0'
  private dirty = false

  constructor(version: ScormVersion, values: Record<string, string>, onCommit: CommitHandler) {
    this.version = version
    this.values = { ...values }
    this.onCommit = onCommit
  }

  get isActive() {
    return this.initialized && !this.terminated
  }

  get hasChanges() {
    return this.dirty
  }

  /** Writable values to send to the server. */
  snapshot(): Record<string, string> {
    const readOnly = this.is12 ? READ_ONLY_12 : READ_ONLY_2004
    return Object.fromEntries(Object.entries(this.values).filter(([key]) => !readOnly.has(key)))
  }

  /** Saves the pending values; `finish` also closes the session. */
  flush(finish = false) {
    if (!this.initialized || this.terminated) return
    if (finish) this.terminated = true
    this.dirty = false
    this.onCommit(this.snapshot(), finish)
  }

  private get is12() {
    return this.version === '1.2'
  }

  private fail(code: string): string {
    this.lastError = code
    return 'false'
  }

  /** Failed GetValue: the SCO receives an empty string. */
  private failGet(code: string): string {
    this.lastError = code
    return ''
  }

  private ok(value = 'true'): string {
    this.lastError = '0'
    return value
  }

  initialize(parameter: string): string {
    if (parameter !== '' && parameter !== undefined && parameter !== null) return this.fail('201')
    if (this.terminated) return this.fail(this.is12 ? '101' : '104')
    if (this.initialized) return this.fail(this.is12 ? '101' : '103')
    this.initialized = true
    return this.ok()
  }

  terminate(parameter: string): string {
    if (parameter !== '' && parameter !== undefined && parameter !== null) return this.fail('201')
    if (!this.initialized) return this.fail(this.is12 ? '301' : '112')
    if (this.terminated) return this.fail(this.is12 ? '101' : '113')
    this.flush(true)
    return this.ok()
  }

  commit(parameter: string): string {
    if (parameter !== '' && parameter !== undefined && parameter !== null) return this.fail('201')
    if (!this.initialized) return this.fail(this.is12 ? '301' : '142')
    if (this.terminated) return this.fail(this.is12 ? '101' : '143')
    this.flush()
    return this.ok()
  }

  private count(key: string): number {
    const prefix = key.slice(0, -'_count'.length)
    const pattern = new RegExp('^' + prefix.replace(/\./g, '\\.') + '(\\d+)\\.')
    const indexes = new Set<string>()
    for (const name of Object.keys(this.values)) {
      const match = name.match(pattern)
      if (match) indexes.add(match[1])
    }
    return indexes.size
  }

  getValue(element: string): string {
    if (!this.initialized) return this.failGet(this.is12 ? '301' : '122')
    if (this.terminated) return this.failGet(this.is12 ? '101' : '123')
    if (!element) return this.failGet(this.is12 ? '201' : '301')

    if (element.endsWith('._children')) {
      const children = (this.is12 ? CHILDREN_12 : CHILDREN_2004)[element]
      return children ? this.ok(children) : this.failGet(this.is12 ? '202' : '301')
    }
    if (element.endsWith('._count')) return this.ok(String(this.count(element)))

    const writeOnly = this.is12
      ? WRITE_ONLY_12.has(element) || /^cmi\.interactions\.\d+\./.test(element)
      : WRITE_ONLY_2004.has(element)
    if (writeOnly) return this.failGet(this.is12 ? '404' : '405')

    if (element in this.values) return this.ok(this.values[element])

    if (element.startsWith('adl.nav.request_valid.')) return this.ok('unknown')
    if (element === 'adl.nav.request') return this.ok('_none_')
    if (!element.startsWith('cmi.')) return this.failGet('401')
    // Known element not set yet
    return this.is12 ? this.ok('') : this.failGet('403')
  }

  setValue(element: string, value: unknown): string {
    if (!this.initialized) return this.fail(this.is12 ? '301' : '132')
    if (this.terminated) return this.fail(this.is12 ? '101' : '133')
    if (!element) return this.fail(this.is12 ? '201' : '351')
    if (!element.startsWith('cmi.') && !element.startsWith('adl.')) return this.fail('401')

    if (element.endsWith('._children') || element.endsWith('._count')) {
      return this.fail(this.is12 ? '402' : '404')
    }
    if ((this.is12 ? READ_ONLY_12 : READ_ONLY_2004).has(element)) {
      return this.fail(this.is12 ? '403' : '404')
    }

    const text = value === null || value === undefined ? '' : String(value)
    const typeError = this.is12 ? '405' : '406'
    const vocabulary = VOCABULARY[element]
    if (vocabulary && !vocabulary.includes(text)) return this.fail(typeError)
    if (/\.score\.(raw|min|max|scaled)$/.test(element) || element === 'cmi.progress_measure') {
      if (text !== '' || !this.is12) {
        if (!NUMERIC.test(text)) return this.fail(typeError)
        const number = Number(text)
        if (element.endsWith('.scaled') && (number < -1 || number > 1)) return this.fail('407')
        if (element === 'cmi.progress_measure' && (number < 0 || number > 1)) return this.fail('407')
      }
    }

    this.values[element] = text
    this.dirty = true
    return this.ok()
  }

  getLastError(): string {
    return this.lastError
  }

  getErrorString(code: string): string {
    return (this.is12 ? ERRORS_12 : ERRORS_2004)[String(code)] || ''
  }

  getDiagnostic(code: string): string {
    return this.getErrorString(code || this.lastError)
  }

  /** Object to expose as window.API (1.2) or window.API_1484_11 (2004). */
  api() {
    if (this.is12) {
      return {
        LMSInitialize: (p: string) => this.initialize(p),
        LMSFinish: (p: string) => this.terminate(p),
        LMSGetValue: (e: string) => this.getValue(e),
        LMSSetValue: (e: string, v: unknown) => this.setValue(e, v),
        LMSCommit: (p: string) => this.commit(p),
        LMSGetLastError: () => this.getLastError(),
        LMSGetErrorString: (c: string) => this.getErrorString(c),
        LMSGetDiagnostic: (c: string) => this.getDiagnostic(c),
      }
    }
    return {
      Initialize: (p: string) => this.initialize(p),
      Terminate: (p: string) => this.terminate(p),
      GetValue: (e: string) => this.getValue(e),
      SetValue: (e: string, v: unknown) => this.setValue(e, v),
      Commit: (p: string) => this.commit(p),
      GetLastError: () => this.getLastError(),
      GetErrorString: (c: string) => this.getErrorString(c),
      GetDiagnostic: (c: string) => this.getDiagnostic(c),
    }
  }
}
